import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import AppLayout from "../components/AppLayout";
import { Empty, ErrorNote, Loading, SectionTitle } from "../components/Feedback";
import RoCrateButton from "../components/RoCrateButton";
import { Badge, Btn, Card, Field, G_BG, G_DARK, Input, Textarea } from "../components/Shared";
import { useApi } from "../useApi";
import DataAccessPanel from "./DataAccessPanel";
import ProjectCrateImport from "./ProjectCrateImport";
import ProjectMembers from "./ProjectMembers";
import { personLabel } from "../components/MemberPicker";
import { DeleteProjectDialog, ProjectSettings } from "./ProjectSettings";
import SqlLoginsPanel from "./SqlLoginsPanel";
import ApiTokensPanel from "./ApiTokensPanel";
import ReleasesPanel from "./ReleasesPanel";
import ArchivePanel from "./ArchivePanel";
import HostingModeBanner from "../components/HostingModeBanner";
import FieldValuesForm from "../forms/FieldValuesForm";
import ProjectFieldSettings from "./ProjectFieldSettings";

// "Hosted at <url>" (task 2026-10-05 "a built database is hosted under its slug with one Host
// button"). The address is the backend's (GET /projects/{id}/hosting), never built here.
function HostedBadge({ projectId }) {
  const { data: h } = useApi(`/projects/${projectId}/hosting`);
  if (!h || !h.hosted) return null;
  return (
    <>
      <div data-testid="hosted-badge" style={{ marginTop: 12, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 13, color: "#374151" }}>
        <Badge color="blue">{h.mode === "archived" ? "archived" : "hosted"}</Badge>
        <span>
          Hosted at{" "}
          {h.url ? <a href={h.url} style={{ fontFamily: "'JetBrains Mono', monospace" }}>{h.url}</a>
                 : <span>the Data pages of this portal (project addresses are off)</span>}
          {`, version ${h.version}`}{h.since ? `, since ${new Date(h.since).toLocaleString()}` : ""}.
        </span>
      </div>
      {/* maintenance or archived (task a-hosted-database-can-be-archived-read-only) */}
      <HostingModeBanner hosting={h} />
    </>
  );
}

// Any member (or an admin) adds a study; its author and the project's curators edit it.
function NewStudyForm({ project, onCancel }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", description: "" });
  const [error, setError] = useState(null);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async e => {
    e.preventDefault();
    setError(null);
    try {
      const s = await api.post(`/projects/${project.id}/studies`, form);
      navigate(`/studies/${s.id}`);
    } catch (err) {
      setError(err);
    }
  };

  return (
    <Card style={{ marginBottom: 18 }}>
      <form onSubmit={submit} aria-label="New study" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <ErrorNote error={error} />
        <Field label="Study name" required><Input value={form.name} onChange={set("name")} /></Field>
        <Field label="Description"><Textarea value={form.description} onChange={set("description")} /></Field>
        <p style={{ margin: 0, fontSize: 13, color: "#4b5563" }}>
          You will be its author: you and the project's curators edit it.
        </p>
        <div style={{ display: "flex", gap: 10 }}>
          <Btn type="submit" disabled={!form.name}>Create study</Btn>
          <Btn variant="secondary" onClick={onCancel}>Cancel</Btn>
        </div>
      </form>
    </Card>
  );
}

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const { user } = useAuth();
  const project = useApi(`/projects/${projectId}`);
  const studies = useApi(`/projects/${projectId}/studies`);
  const [creating, setCreating] = useState(false);
  const [imported, setImported] = useState(false);   // keep the import result on screen
  const [settings, setSettings] = useState(false);   // the project settings card is open
  const [deleting, setDeleting] = useState(false);   // the Delete project dialog is open
  const [fieldSettings, setFieldSettings] = useState(false);   // the Field settings card is open
  const [fieldsVersion, setFieldsVersion] = useState(0);       // remounts the values after a change
  const p = project.data;
  // the server's rule (deps.can_create_in_project): a member of the project, or an admin
  const canAddStudy = !!p && (!!user?.is_admin || p.my_role != null);
  const cold = !!p && ["cold", "restoring"].includes(p.archive_state);

  return (
    <AppLayout title={p ? p.name : "Project"} crumbs={[{ label: "Projects", to: "/projects" }, { label: p ? p.name : "…" }]}>
      <ErrorNote error={project.error} />
      {project.loading && <Loading />}
      {p && (
        <>
          <Card style={{ marginBottom: 24, padding: "20px 24px" }}>
            {p.description && <p style={{ margin: "0 0 12px", fontSize: 14, color: "#374151" }}>{p.description}</p>}
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 13, color: "#6b7280" }}>
              <span>Curator{(p.curators || []).length === 1 ? "" : "s"}:</span>
              {(p.curators || []).map(c => <Badge key={c.id}>{personLabel(c)}</Badge>)}
              <span>{p.member_count} member{p.member_count === 1 ? "" : "s"}</span>
              {p.my_role && <span>· you are a {p.my_role}</span>}
              <span style={{ marginLeft: 12, fontFamily: "'JetBrains Mono', monospace" }}>slug: {p.slug}</span>
            </div>
            {p.hosted_version != null && <HostedBadge projectId={p.id} />}
            {p.inactive_since && (
              <p data-inactive style={{ margin: "10px 0 0", fontSize: 13, color: "#4b5563" }}>
                <Badge color="gray">inactive</Badge>{" "}
                {p.inactive_reason === "no_active_member"
                  ? "None of its members is active any more."
                  : "Nothing happened in this project for a long time."}{" "}
                It is only a label: everything works as before, and any activity makes it active again.
              </p>
            )}
            <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Link
                to={`/projects/${p.id}/dbdesign`}
                style={{ padding: "9px 20px", borderRadius: 8, fontSize: 14, fontWeight: 600, textDecoration: "none",
                         background: G_BG, color: G_DARK, border: "1px solid rgba(0,150,64,0.5)" }}
              >
                Database design
              </Link>
              {p.hosted_version != null && (
                <Link
                  to={`/projects/${p.id}/data`}
                  style={{ padding: "9px 20px", borderRadius: 8, fontSize: 14, fontWeight: 600, textDecoration: "none",
                           background: G_BG, color: G_DARK, border: "1px solid rgba(0,150,64,0.5)" }}
                >
                  Data
                </Link>
              )}
              <Link
                to={`/projects/${p.id}/log`}
                style={{ padding: "9px 20px", borderRadius: 8, fontSize: 14, fontWeight: 600, textDecoration: "none",
                         background: G_BG, color: G_DARK, border: "1px solid rgba(0,150,64,0.5)" }}
              >
                Log
              </Link>
              <RoCrateButton kind="projects" id={p.id} filename={`project-${p.slug}.zip`} />
              {p.can_manage && (
                <Btn variant="secondary" onClick={() => setSettings(s => !s)}>Project settings</Btn>
              )}
              {p.can_manage && (
                <Btn variant="secondary" onClick={() => setFieldSettings(s => !s)}>Field settings</Btn>
              )}
              {user?.is_admin && <Btn variant="danger" onClick={() => setDeleting(true)}>Delete project</Btn>}
              {/* A project crate goes only into a fresh project (the backend refuses one with studies). */}
              {p.can_manage && studies.data && (studies.data.length === 0 || imported) && (
                <ProjectCrateImport project={p}
                                    onImported={() => { setImported(true); studies.reload(); project.reload(); }} />
              )}
            </div>
          </Card>
          {settings && p.can_manage && (
            <ProjectSettings project={p} onSaved={project.reload} onClose={() => setSettings(false)} />
          )}
          {fieldSettings && p.can_manage && (
            <ProjectFieldSettings project={p} onSaved={() => setFieldsVersion(v => v + 1)}
                                  onClose={() => setFieldSettings(false)} />
          )}
          {/* The project card's additional fields (N6): its curators fill them in. */}
          <FieldValuesForm key={fieldsVersion} path={`/projects/${p.id}/field-values`} canEdit={!!p.can_manage}
                           title="Project fields" formLabel={`Fields of ${p.name}`}
                           projectId={p.id} canManageProject={!!p.can_manage}
                           hint={<>Define the project's fields, or add them from templates, with <strong>Field settings</strong> above.</>} />
          {deleting && <DeleteProjectDialog project={p} onClose={() => setDeleting(false)} />}
          <ProjectMembers project={p} onChanged={project.reload} />

          {/* A cold-archived database is dropped: nothing to read until an admin restores it. */}
          {p.hosted_version != null && !cold && <DataAccessPanel projectId={p.id} />}
          {p.hosted_version != null && !cold && <SqlLoginsPanel projectId={p.id} />}
          {p.hosted_version != null && !cold && <ApiTokensPanel projectId={p.id} />}
          {p.hosted_version != null && <ArchivePanel projectId={p.id} />}
          {/* Releases outlive hosting: the panel shows itself while hosted or when any exists. */}
          <ReleasesPanel projectId={p.id} slug={p.slug} />

          <SectionTitle right={canAddStudy && !creating && <Btn onClick={() => setCreating(true)}>New study</Btn>}>
            Studies
          </SectionTitle>
          {creating && <NewStudyForm project={p} onCancel={() => setCreating(false)} />}
          <ErrorNote error={studies.error} />
          {studies.loading ? <Loading /> : (studies.data || []).length === 0 ? (
            <Empty>No studies yet.</Empty>
          ) : (
            <div style={{ display: "grid", gap: 12 }}>
              {studies.data.map(s => (
                <Card key={s.id} style={{ padding: "16px 22px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                    <Link to={`/studies/${s.id}`} style={{ fontSize: 16, fontWeight: 600, color: "#1a1a1a" }}>{s.name}</Link>
                    <span style={{ display: "flex", gap: 6 }}>
                      {s.created_by && <Badge color="blue">by {s.created_by.username}</Badge>}
                      {s.created_by && user && s.created_by.id === user.id && <Badge color="green">yours</Badge>}
                    </span>
                  </div>
                  {s.description && <p style={{ margin: "6px 0 0", fontSize: 14, color: "#374151" }}>{s.description}</p>}
                  <p style={{ margin: "6px 0 0", fontSize: 13, color: "#6b7280" }}>
                    {s.dataset_count} dataset{s.dataset_count === 1 ? "" : "s"}
                  </p>
                </Card>
              ))}
            </div>
          )}
        </>
      )}
    </AppLayout>
  );
}
